/**
 * Project template endpoints — Phase 4 vertical starter packs (June 10, 2026).
 *
 * Backs the FE TemplatesBrowser dialog rendered from the ProjectHub's
 * "+ Nuovo ▾" split-button. The catalog is curated server-side; the
 * clone path returns a fresh ``Project`` shaped exactly like
 * ``createProject``, so the FE redirects to ``/workspace/projects/$id``
 * immediately.
 *
 * BE source: ``src/api/v1/endpoints/project_templates.py``.
 */

import type { Project } from "../../domain/project.js";
import { apiClient } from "../ApiClient.js";

/**
 * Allowed verticals — kept in sync with the BE seed. The union is open
 * to ``string`` because future user-published templates (Phase 5
 * marketplace) may introduce new verticals at runtime.
 */
export type ProjectTemplateVertical =
  | "commercialista"
  | "pmi"
  | "studio-legale"
  | "studio-notarile"
  | "ingegneria"
  | "architettura"
  | "sales"
  | "marketing"
  | "e-commerce"
  | "customer-success"
  | "hr"
  | "it-devops"
  | (string & {});

export interface ProjectTemplatePersonaSeed {
  instructions?: string | null;
  persona_role?: string | null;
  persona_tone?: string | null;
  default_agent_mode_version_id?: string | null;
}

export interface ProjectTemplate {
  template_id: string;
  slug: string;
  name: string;
  vertical: ProjectTemplateVertical;
  icon: string | null;
  color: string | null;
  description: string | null;
  long_description: string | null;
  required_capabilities: readonly string[];
  persona_seed: ProjectTemplatePersonaSeed;
  default_folder_paths: readonly string[];
  is_official: boolean;
  position: number;
}

export interface CreateProjectFromTemplateInput {
  /** Either ``template_id`` OR ``slug`` — exactly one. */
  template_id?: string;
  slug?: string;
  /** Optional override of the template's default project name. */
  name?: string;
}

/** GET ``/project-templates`` — curated catalog, optionally filtered by vertical. */
export async function listProjectTemplates(
  vertical?: string,
): Promise<readonly ProjectTemplate[]> {
  const qs = vertical ? `?vertical=${encodeURIComponent(vertical)}` : "";
  return await apiClient.get<readonly ProjectTemplate[]>(
    `/project-templates${qs}`,
  );
}

/** GET ``/project-templates/{slug}`` — fetch a single template by stable slug. */
export async function getProjectTemplate(slug: string): Promise<ProjectTemplate> {
  return await apiClient.get<ProjectTemplate>(
    `/project-templates/${encodeURIComponent(slug)}`,
  );
}

/**
 * POST ``/projects/from-template`` — clone a template into a fresh
 * project. Returns the freshly-created project so the caller can
 * navigate to it directly (no second roundtrip needed to look it up).
 */
export async function createProjectFromTemplate(
  input: CreateProjectFromTemplateInput,
): Promise<Project> {
  return await apiClient.post<Project>("/projects/from-template", input);
}
